"use client";

import Image from "next/image";
import { useContactModal } from "@/components/ContactContext";

/**
 * Container — max-width wrapper used by every section.
 * TagJS макет тянется до ~1548-1600px на десктопе; держу 1440 как разумный
 * максимум для современных дисплеев, иначе ассеты выглядят растянутыми.
 */
export function Container({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`mx-auto w-full max-w-[1440px] px-4 sm:px-8 lg:px-[60px] xl:px-[80px] ${className}`}>
      {children}
    </div>
  );
}

/**
 * Section — vertical rhythm wrapper. TagJS gap между секциями ≈ 85px на десктопе,
 * 46-58px на мобильном. Используем единый ритм во всех блоках.
 */
export function Section({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`pb-12 sm:pb-16 lg:pb-[85px] ${className}`}>
      {children}
    </section>
  );
}

/**
 * BlueButton — primary CTA "Получить предложение".
 * Открывает существующий RequestModal через useContactModal (api/lead).
 * Стрелка — ассет из TagJS макета (одна для всех кнопок).
 */
export function BlueButton({
  label = "Получить предложение",
  className = "",
}: {
  label?: string;
  className?: string;
}) {
  const { open } = useContactModal();
  return (
    <button
      type="button"
      onClick={open}
      className={`inline-flex items-center gap-2 rounded-[70px] bg-primary px-6 py-3.5 text-[15px] font-medium text-white transition hover:bg-primary-hover sm:px-8 sm:py-4 sm:text-base ${className}`}
    >
      <span>{label}</span>
      <Image
        src="/img/tagjs/yd1xihum.png"
        alt=""
        width={16}
        height={16}
        className="rounded-full"
      />
    </button>
  );
}

/**
 * SectionTitle — заголовок 32-38px по TagJS.
 * Может быть на чёрном фоне (text-white) или на сером (text-text).
 */
export function SectionTitle({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={`text-[28px] font-medium leading-[1.05] tracking-[-0.5px] text-text sm:text-[32px] lg:text-[38px] xl:tracking-[-1px] ${className}`}
    >
      {children}
    </h2>
  );
}

/**
 * BodyText — основной серый параграф с подобранным размером TagJS (≈18px десктоп).
 */
export function BodyText({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`text-[14px] leading-[1.45] tracking-[-0.2px] text-text-muted sm:text-[15px] lg:text-[17px] xl:text-[18px] ${className}`}
    >
      {children}
    </p>
  );
}
